import React, { useState } from 'react';
import { Mic, Sparkles, Lock, ArrowRight, Volume2 } from 'lucide-react';

/**
 * VoiceBotButton - A prominent button to send a voice conversational bot to meetings
 * 
 * For access=true users: Opens the voice bot meeting flow
 * For access=false users: Opens the Waitlist modal
 */
const VoiceBotButton = ({
    user,
    darkMode,
    onVoiceBotClick,
    onWaitlistClick,
    className = '',
    compact = false
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const hasAccess = user?.access === true;

    const handleClick = () => {
        if (hasAccess) {
            onVoiceBotClick?.();
        } else {
            onWaitlistClick?.();
        }
    };

    // Compact version for sidebar or smaller spaces
    if (compact) {
        return (
            <button
                onClick={handleClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className={`
          group relative flex items-center gap-3 w-full px-4 py-3 rounded-2xl
          font-bold text-sm transition-all duration-300
          ${hasAccess
                        ? 'bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-[1.02]'
                        : darkMode
                            ? 'bg-slate-800 border border-slate-700 text-slate-300 hover:border-purple-500/50'
                            : 'bg-slate-100 border border-slate-200 text-slate-600 hover:border-purple-400'}
          ${className}
        `}
            >
                <div className={`
          p-2 rounded-xl transition-all
          ${hasAccess
                        ? 'bg-white/20'
                        : darkMode ? 'bg-slate-700' : 'bg-slate-200'}
        `}>
                    <Volume2 size={18} className={hasAccess ? 'text-white' : 'text-purple-500'} />
                </div>
                <span className="flex-1 text-left">Voice Bot</span>
                {!hasAccess && (
                    <Lock size={14} className="text-slate-400" />
                )}
                {hasAccess && (
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                )}
            </button>
        );
    }

    // Full/prominent version
    return (
        <button
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={`
        group relative w-full overflow-hidden rounded-2xl p-[2px]
        transition-all duration-300 hover:scale-[1.01]
        ${hasAccess
                    ? 'bg-gradient-to-r from-violet-600 via-purple-500 to-indigo-600 shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50'
                    : 'bg-gradient-to-r from-slate-600 via-slate-500 to-slate-600'}
        ${className}
      `}
        >
            {/* Inner content */}
            <div className={`
        relative flex items-center gap-4 px-5 py-4 rounded-[14px] transition-all
        ${hasAccess
                    ? 'bg-gradient-to-r from-violet-600/90 via-purple-600/90 to-indigo-600/90'
                    : darkMode
                        ? 'bg-slate-900/95 hover:bg-slate-900/80'
                        : 'bg-white/95 hover:bg-white/80'}
      `}>
                {/* Animated icon container */}
                <div className={`
          relative p-3 rounded-xl transition-all duration-300
          ${hasAccess
                        ? 'bg-white/20 group-hover:bg-white/30'
                        : darkMode
                            ? 'bg-purple-500/10'
                            : 'bg-purple-50'}
        `}>
                    {/* Pulse animation ring for access users */}
                    {hasAccess && (
                        <div className="absolute inset-0 rounded-xl bg-white/30 animate-ping opacity-30" />
                    )}
                    <Volume2
                        size={24}
                        className={`relative z-10 transition-transform group-hover:scale-110 ${hasAccess ? 'text-white' : 'text-purple-500'
                            }`}
                    />
                </div>

                {/* Text content */}
                <div className="flex-1 text-left">
                    <div className={`font-bold text-base ${hasAccess
                            ? 'text-white'
                            : darkMode ? 'text-slate-200' : 'text-slate-800'
                        }`}>
                        Send Voice Bot to Meeting
                    </div>
                    <div className={`text-xs mt-0.5 ${hasAccess
                            ? 'text-white/70'
                            : darkMode ? 'text-slate-400' : 'text-slate-500'
                        }`}>
                        {hasAccess
                            ? 'AI joins and speaks in your meetings'
                            : 'Join the waitlist to unlock'}
                    </div>
                </div>

                {/* Right side indicator */}
                <div className={`
          flex items-center gap-2 transition-all
          ${hasAccess ? 'text-white' : darkMode ? 'text-slate-400' : 'text-slate-500'}
        `}>
                    {!hasAccess && (
                        <span className={`
              text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full
              ${darkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'}
            `}>
                            Coming Soon
                        </span>
                    )}
                    {hasAccess ? (
                        <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                    ) : (
                        <Lock size={18} />
                    )}
                </div>

                {/* Shimmer effect for access users */}
                {hasAccess && (
                    <div className="absolute inset-0 overflow-hidden rounded-[14px] pointer-events-none">
                        <div
                            className={`absolute inset-0 translate-x-[-100%] bg-gradient-to-r from-transparent via-white/10 to-transparent
                ${isHovered ? 'animate-shimmer' : ''}`}
                            style={{
                                animation: isHovered ? 'shimmer 1s ease-in-out' : 'none'
                            }}
                        />
                    </div>
                )}
            </div>

            <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
        </button>
    );
};

export default VoiceBotButton;
